import { useHqScreenEvent, trackHq } from './analytics';
import { HQ_DESCRIPTION, HQ_NAME } from './config';
import { HqLayout } from './layout';
import { HqLink } from './Link';
import { HqHarborBand, HqKicker } from './ui';
import { harborUrl } from './urls';

export function AboutPage() {
  useHqScreenEvent('HQ About Screen');

  return (
    <HqLayout
      title={`About: ${HQ_NAME}`}
      description='Co-parenting HQ is a free starting place for coparents: guides and tools you can use without an account. Built by the team behind Harbor. Not legal advice.'
      path='/about'
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: `About ${HQ_NAME}`,
        url: 'https://coparentinghq.com/about',
        description: HQ_DESCRIPTION,
        isPartOf: {
          '@type': 'WebSite',
          name: HQ_NAME,
          url: 'https://coparentinghq.com/',
        },
      }}
    >
      <article className='max-w-2xl'>
        <HqKicker>About</HqKicker>
        <h1 className='mt-2 font-hq text-4xl font-medium tracking-tight sm:text-5xl'>
          A starting place, not a product pitch
        </h1>
        <p className='mt-4 text-lg leading-relaxed text-hq-muted'>
          {HQ_NAME} is a free library for people who are about to share kids or already are. You can
          read a guide, print a checklist, or use a schedule or expense tool without creating an
          account.
        </p>

        <section className='mt-10'>
          <h2 className='font-hq text-2xl font-medium tracking-tight'>Who it is for</h2>
          <p className='mt-3 leading-relaxed text-hq-body'>
            People in the first week after a separation. People who have been coparenting for years
            and still do not have a calendar both houses trust. People whose order already names
            another app and who still need a private, facts-only file.
          </p>
        </section>

        <section className='mt-10'>
          <h2 className='font-hq text-2xl font-medium tracking-tight'>What is free</h2>
          <p className='mt-3 leading-relaxed text-hq-body'>
            The guides, the checklists, and the standalone tools linked from{' '}
            <HqLink to='/tools' className='font-medium text-hq-sage hover:underline'>
              /tools
            </HqLink>
            . You can email a checklist to yourself. We do not charge for reading this site.
          </p>
        </section>

        <section className='mt-10'>
          <h2 className='font-hq text-2xl font-medium tracking-tight'>How this relates to Harbor</h2>
          <p className='mt-3 leading-relaxed text-hq-body'>
            {HQ_NAME} is built by the team behind{' '}
            <a
              href={harborUrl('/')}
              className='font-medium text-hq-sage hover:underline'
              onClick={() => trackHq('HQ Harbor CTA Pressed', { location: 'about' })}
            >
              Harbor
            </a>
            , a coparenting app with a shared calendar, expense ledger, and private journal. This
            site is meant to be useful even if you never open Harbor. Harbor is the next step if
            both parents want the same week and the same ledger, or if you want a private file that
            is not a notebook.
          </p>
          <p className='mt-3 leading-relaxed text-hq-body'>
            If a court order names OurFamilyWizard, TalkingParents, or another app, use that app
            for whatever the order covers. Harbor does not replace a court-named channel.
          </p>
        </section>

        <section className='mt-10'>
          <h2 className='font-hq text-2xl font-medium tracking-tight'>Not legal advice</h2>
          <p className='mt-3 leading-relaxed text-hq-body'>
            Nothing here is legal advice, a lawyer referral, or a substitute for counsel in your
            state. If you are in danger, call local emergency services. In the US you can call or
            text 988, or 1-800-799-7233 for the domestic violence hotline.
          </p>
        </section>
      </article>

      <div className='mt-14'>
        <HqHarborBand location='about-band' />
      </div>
    </HqLayout>
  );
}
